import { memo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { VideoDialog } from "@/components/VideoDialog";

const Hero = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-16 bg-gradient-hero">
      {/* Animated mesh gradient background */}
      <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
      
      {/* Animated gradient orbs with proper spacing */}
      <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-primary/30 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-accent/25 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] bg-secondary/20 rounded-full blur-[90px] animate-pulse" style={{ animationDelay: '2s' }} />

      {/* Content with proper spacing */}
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto text-center space-y-10">
          {/* Badge with proper spacing */}
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-card/30 backdrop-blur-xl border border-white/20 shadow-glass">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-foreground">{t('hero.badge')}</span>
          </div>

          {/* Heading with improved spacing */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight px-4" style={{ willChange: 'contents' }}>
            <span className="block mb-4 text-foreground">
              {t('hero.title.line1')}
            </span>
            <span className="block bg-gradient-primary bg-clip-text text-transparent">
              {t('hero.title.line2')}
            </span>
          </h1>

          {/* Subheading with proper margins */}
          <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto px-4 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* Interactive Video Showcase */}
          <div 
            onClick={() => setVideoOpen(true)}
            className="group relative cursor-pointer max-w-md mx-auto my-8 p-8 rounded-3xl bg-gradient-to-b from-primary/20 to-accent/10 border-2 border-primary/30 hover:border-primary/60 transition-all duration-500 hover:scale-105 hover:shadow-[0_0_60px_rgba(var(--primary),0.4)]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
            <div className="relative space-y-4">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/20 backdrop-blur-xl border border-primary/40 mb-4 group-hover:bg-primary/30 transition-all duration-300">
                  <Play className="w-8 h-8 text-primary fill-primary group-hover:scale-110 transition-transform" />
                </div>
                <p className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-2">
                  {t('hero.videoTitle')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('hero.videoSubtitle')}
                </p>
              </div>
            </div>
          </div>

          {/* CTA Buttons with consistent spacing */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
            <Link to="/courses">
              <Button variant="hero" size="xl" className="group w-full sm:w-auto min-w-[200px]">
                {t('hero.browseCourses')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" size="xl" className="w-full sm:w-auto min-w-[200px]">
                {t('hero.learnMore')}
              </Button>
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="pt-16 max-w-4xl mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div className="text-center p-6 md:p-8 rounded-2xl bg-card/30 backdrop-blur-xl border border-white/10 shadow-glass hover:shadow-glass-hover transition-all duration-300">
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-3 break-words">{t('hero.stats.smart')}</div>
                <div className="text-sm md:text-base text-muted-foreground break-words">{t('hero.stats.smartDesc')}</div>
              </div>
              <div className="text-center p-6 md:p-8 rounded-2xl bg-card/30 backdrop-blur-xl border border-white/10 shadow-glass hover:shadow-glass-hover transition-all duration-300">
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-3 break-words">{t('hero.stats.live')}</div>
                <div className="text-sm md:text-base text-muted-foreground break-words">{t('hero.stats.liveDesc')}</div>
              </div>
              <div className="text-center p-6 md:p-8 rounded-2xl bg-card/30 backdrop-blur-xl border border-white/10 shadow-glass hover:shadow-glass-hover transition-all duration-300">
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-3 break-words">{t('hero.stats.pro')}</div>
                <div className="text-sm md:text-base text-muted-foreground break-words">{t('hero.stats.proDesc')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade with proper positioning */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      
      <VideoDialog 
        open={videoOpen} 
        onOpenChange={setVideoOpen}
        videoId="I64IuEE1x2I"
        title={t('hero.videoTitle')}
      />
    </section>
  );
};

export default memo(Hero);
